import React, { useState } from 'react';
import styled from 'styled-components';
import { FiChevronRight } from 'react-icons/fi';

const FAQContainer = styled.section`
  max-width: 900px;
  margin: 3rem auto;
  background: white;
  border-radius: 24px;
  box-shadow: 0 4px 24px rgba(143, 226, 0, 0.07);
  padding: 2.5rem 1.5rem 2rem 1.5rem;

  @media (max-width: 700px) {
    margin: 1.5rem 0;
    padding: 1.125rem 2vw 1.125rem 2vw;
    border-radius: 12px;
  }
`;

const FAQTitle = styled.h1`
  font-size: 2.4rem;
  font-weight: 800;
  color: ${props => props.theme.colors.secondary};
  margin-bottom: 2rem;
  text-align: center;

  @media (max-width: 700px) {
    font-size: 1.3rem;
    margin-bottom: 1.125rem;
  }
`;

const FAQList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const FAQItem = styled.li`
  border-bottom: 1.5px solid #eaf4eb;
  margin-bottom: 0;

  &:last-child {
    border-bottom: none;
  }
`;

const FAQQuestion = styled.button`
  background: none;
  border: none;
  width: 100%;
  text-align: left;
  font-size: 1.18rem;
  font-weight: 700;
  color: ${props => props.theme.colors.heading};
  padding: 1.375rem 0 0.75rem 0;
  cursor: pointer;
  outline: none;
  transition: color 0.2s;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;

  &:focus {
    color: ${props => props.theme.colors.secondary};
  }

  @media (max-width: 700px) {
    font-size: 1.01rem;
    padding: 0.875rem 0 0.5rem 0;
  }
`;

const FAQToggle = styled(FiChevronRight)`
  font-size: 1.5rem;
  color: ${props => props.theme.colors.secondary};
  transition: transform 0.2s;
  flex-shrink: 0;
  transform: ${props => props.isOpen ? 'rotate(90deg)' : 'rotate(0deg)'};
`;

const FAQAnswer = styled.div`
  max-height: ${props => props.isOpen ? '400px' : '0'};
  overflow: hidden;
  font-size: 1.05rem;
  color: #444;
  background: #eaf4eb;
  border-radius: 0 0 16px 16px;
  margin-bottom: ${props => props.isOpen ? '0.625rem' : '0'};
  padding: ${props => props.isOpen ? '0.625rem 1.125rem 1.125rem 1.125rem' : '0 1.125rem'};
  transition: max-height 0.35s cubic-bezier(0.4, 0, 0.2, 1), padding 0.2s, margin-bottom 0.2s;

  @media (max-width: 700px) {
    font-size: 0.98rem;
    padding: ${props => props.isOpen ? '0.5rem 0.5rem 0.75rem 0.5rem' : '0 0.5rem'};
  }

  b {
    font-weight: 600;
    color: #111;
  }
`;

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: 'What is Recognition of Prior Learning (RPL)?',
      answer: 'RPL is an assessment process that lets you gain formal qualifications for skills and knowledge you already have from work, training or life experience.'
    },
    {
      question: 'Who is eligible for RPL?',
      answer: 'Anyone with relevant work experience, skills, or informal training in an industry may be eligible for RPL.'
    },
    {
      question: 'How does the RPL process work?',
      answer: 'You provide evidence of your skills and experience, which is assessed by a Registered Training Organisation (RTO) to see if you meet the requirements for a qualification.'
    },
    {
      question: 'Do I need to study if I apply for RPL?',
      answer: 'No, if your existing skills and experience meet the qualification requirements, you won\'t need to study those units again.'
    },
    {
      question: 'What type of evidence can I provide?',
      answer: 'Evidence may include work samples, photos, videos, references, your resume, prior certificates, job descriptions, and letters from employers.'
    },
    {
      question: 'How long does the RPL process take?',
      answer: 'It varies by person and qualification, but many candidates complete the RPL process in a few weeks to a few months.'
    },
    {
      question: 'Are RPL qualifications nationally recognised?',
      answer: 'Yes — qualifications awarded through RPL are issued by approved RTOs and are recognised throughout Australia.'
    },
    {
      question: 'What industries can I get RPL for?',
      answer: 'RPL is available for many industries, including construction, engineering, automotive, hospitality, community services, and more.'
    },
    {
      question: 'Is RPL the same as credit transfer?',
      answer: 'No — RPL recognises informal and non-accredited learning, while credit transfer only applies to formal studies already completed.'
    },
    {
      question: 'Can I use my RPL qualification for licensing?',
      answer: 'Yes, many trade and technical licences require formal qualifications — RPL can help you meet those requirements.'
    },
    {
      question: 'How much does RPL cost?',
      answer: 'Costs vary depending on the qualification, industry and evidence needed — your consultant will give you a clear quote upfront.'
    },
    {
      question: 'Do I have to stop working during RPL?',
      answer: 'No — RPL is designed to fit around your work. You gather evidence while continuing your job.'
    },
    {
      question: 'Can overseas work experience count towards RPL?',
      answer: 'Yes — overseas experience and qualifications can be used as evidence if they\'re relevant to the Australian qualification.'
    },
    {
      question: 'What happens if I don\'t have enough evidence?',
      answer: 'Your assessor may suggest ways to fill any gaps, such as doing short training units to meet all requirements.'
    },
    {
      question: 'Who assesses my evidence?',
      answer: 'A qualified assessor from a Registered Training Organisation will review your portfolio and decide if you meet the standards.'
    },
    {
      question: 'Can RPL help with career change?',
      answer: 'Yes — RPL is useful for formalising your experience so you can move into new roles, industries or apply for higher positions.'
    },
    {
      question: 'What qualifications can I get through RPL?',
      answer: 'You can gain Certificates, Diplomas and other nationally recognised qualifications, depending on your skills and experience.'
    },
    {
      question: 'What if my RPL application is unsuccessful?',
      answer: 'If you don\'t fully meet the requirements, you may be offered training to cover any gaps and complete the qualification.'
    },
    {
      question: 'Do I need to be an Australian citizen for RPL?',
      answer: 'No, but you may need to provide proof of your right to work or live in Australia depending on the RTO\'s policies.'
    },
    {
      question: 'How do I get started with RPL?',
      answer: 'Start by taking a free skills check or speaking to an RPL consultant to find out what qualifications match your experience.'
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const handleKeyDown = (event, index) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleFAQ(index);
    }
  };

  return (
    <FAQContainer>
      <FAQTitle>Frequently Asked Questions</FAQTitle>
      <FAQList>
        {faqs.map((faq, index) => (
          <FAQItem key={index}>
            <FAQQuestion
              onClick={() => toggleFAQ(index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              aria-expanded={openIndex === index}
              aria-controls={`faq-answer-${index}`}
            >
              {faq.question}
              <FAQToggle isOpen={openIndex === index} />
            </FAQQuestion>
            <FAQAnswer
              id={`faq-answer-${index}`}
              isOpen={openIndex === index}
              aria-hidden={openIndex !== index}
            >
              {faq.answer}
            </FAQAnswer>
          </FAQItem>
        ))}
      </FAQList>
    </FAQContainer>
  );
};

export default FAQPage;